import { tagged } from "@mirage/service-logging";
import { getCachedQuerySuggestionsOrFetch } from "@mirage/service-query-suggestions";
import Sentry from "@mirage/shared/sentry";
import { useEffect, useMemo, useRef, useState } from "react";

import type { DemoPromptSuggestion } from "@mirage/growth/onboarding/hooks/useDemoQuerySuggestions";
import { useShowBridgeUserSuggestions } from "@mirage/growth/onboarding/hooks/useShowBridgeUserSuggestions";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type { QuerySuggestionsOptions } from "@mirage/service-query-suggestions/types";

const logger = tagged("useQuerySuggestions");

export const useQuerySuggestions = ({
  demoSuggestions = [],
  shouldFetchMaxSuggestions = false,
  maxSuggestions,
  options,
}: {
  maxSuggestions?: number;
  shouldFetchMaxSuggestions?: boolean;
  demoSuggestions?: (DemoPromptSuggestion | QuerySuggestionResult)[];
  options: QuerySuggestionsOptions;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [retrievedSuggestions, setRetrievedSuggestions] = useState<
    QuerySuggestionResult[]
  >([]);
  const suggestionsFetched = useRef(false);
  const documentIdsStr = options.documentIds?.join(",");
  const documentInfoStr = JSON.stringify(options.documentInfos, (_, value) =>
    typeof value === "bigint" ? Number(value) : value,
  );

  const shouldShowBridgeUserSuggestions = useShowBridgeUserSuggestions();
  const shouldPrioritizeDemoSuggestions = useMemo(() => {
    return shouldShowBridgeUserSuggestions && demoSuggestions?.length === 3;
  }, [shouldShowBridgeUserSuggestions, demoSuggestions]);

  // Reset suggestions when stackId or documentIds change
  useEffect(() => {
    // Provide a placeholder for the suggestions
    setRetrievedSuggestions(Array(maxSuggestions).fill({ text: "" }));
    suggestionsFetched.current = false;
    setIsLoading(true);
  }, [
    options.stackId,
    documentIdsStr,
    documentInfoStr,
    maxSuggestions,
    options.fssSearchScope,
  ]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (suggestionsFetched.current) {
        return;
      }
      suggestionsFetched.current = true;
      try {
        const nextSuggestions = await getCachedQuerySuggestionsOrFetch({
          ...options,
          maxSuggestions: shouldFetchMaxSuggestions
            ? maxSuggestions
            : undefined,
          cacheCapacityUsefulnessThreshold: maxSuggestions,
          shouldHydrateCache: true,
        });

        logger.debug("Retrieved query-suggestions size: ", {
          size: nextSuggestions.length,
        });

        if (nextSuggestions?.length) {
          const filteredSuggestions = nextSuggestions.filter(
            (suggestion) => !suggestion.isStaticSuggestion,
          );
          setRetrievedSuggestions(filteredSuggestions.slice(0, maxSuggestions));
        } else {
          setRetrievedSuggestions([]);
        }
      } catch (error) {
        logger.debug("Fetching query suggestions failed");
        Sentry.captureException(error);
        setRetrievedSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [maxSuggestions, options]);

  // Use useMemo to compute the combined suggestions only when dependencies change
  const querySuggestions = useMemo(() => {
    let suggestions = [...retrievedSuggestions];

    if (demoSuggestions?.length) {
      if (shouldPrioritizeDemoSuggestions) {
        suggestions = [...demoSuggestions];
      } else {
        let demoSuggestionsPartial = demoSuggestions;
        if (maxSuggestions) {
          demoSuggestionsPartial = demoSuggestions.slice(
            0,
            Math.max(0, maxSuggestions - retrievedSuggestions.length),
          );
        }
        suggestions = [...retrievedSuggestions, ...demoSuggestionsPartial];
      }
    }

    return suggestions;
  }, [
    demoSuggestions,
    retrievedSuggestions,
    maxSuggestions,
    shouldPrioritizeDemoSuggestions,
  ]);

  return { isLoading, querySuggestions };
};
